import api from "./api";
import { notifyWorkspaceChanged } from "./workspaceSync";

export type SyncRun = {
  run_id: number; status: string; total: number; completed: number;
  current_kpi?: string | null; started_at: string; retry_at?: string | null;
  synced_count: number; failed_count: number; skipped_count: number;
  period_count: number; written: number; verified: number;
  items: { node_id: string; label: string; error?: string; message?: string; status: string }[];
};
export const syncRunActive = (run: SyncRun) => ["queued", "running", "retry_wait"].includes(run.status);
export const syncRunMessage = (run: SyncRun) => {
  const progress = `Sync #${run.run_id}: ${run.completed}/${run.total} steps complete. ${run.verified} periods verified, ${run.written} changed.`;
  if (syncRunActive(run)) return `${progress} ${run.status === "retry_wait" ? "Temporary failure; retry scheduled automatically." : run.current_kpi ? `Working on ${run.current_kpi}.` : "Queued; saved progress will resume automatically."} You can leave this page.`;
  const problems = run.items.filter((item) => item.status !== "success");
  return `${progress} ${run.status === "success" ? "Finished successfully." : `Finished with ${run.status}.`} ${problems.slice(0, 3).map((item) => `${item.label}: ${item.error || item.message || item.status}`).join("; ")}`.trim();
};

// The server journal, not a browser timer or a guessed timestamp, identifies
// completion. Losing the connection never submits another request.
export async function waitForSyncRun(enterpriseId: number, initial: SyncRun, onProgress: (message: string) => void): Promise<SyncRun> {
  let run = initial;
  let verified = -1;
  while (true) {
    onProgress(syncRunMessage(run));
    if (run.verified !== verified) {
      verified = run.verified;
      if (verified > 0) notifyWorkspaceChanged(enterpriseId, "api-kpi-sync-progress");
    }
    if (!syncRunActive(run)) return run;
    await new Promise((resolve) => window.setTimeout(resolve, 3000));
    try {
      run = (await api.get(`/enterprises/${enterpriseId}/kpis/sync-runs/${initial.run_id}`, { timeout: 20000 })).data;
    } catch (error: any) {
      if ([401, 403, 404].includes(error?.response?.status)) throw error;
      onProgress(`Sync #${initial.run_id} is saved on the server. Reconnecting to its progress…`);
      await new Promise((resolve) => window.setTimeout(resolve, 12000));
    }
  }
}
